import React, { useContext, useEffect, useState } from "react";

// material-ui
import { styled, useTheme } from "@mui/material/styles";
import { AppBar, Box, CssBaseline, Toolbar, useMediaQuery } from "@mui/material";

// project imports
import Header from "./MainLayout/Header";
import Sidebar from "./MainLayout/Sidebar";

import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";

import Loading from "./components/Loading";
import { HEADER_HEIGHT, SIDEBAR_WIDTH } from "./theme";

import { UserContext } from "./state/userState";
import { permissionMap } from "./permissionHandler";
import { MainContext } from "./state/state";
import { UploadManagerProvider } from "./state/uploadManager";
import Modal from "./modal";
import sendRequest from "./helpers/service";
import ForbiddenPage from "./screens/ErrorPages/403";
import { firstReadablePath } from "./helpers/landingPage";

import LiveEdit from "./screens/LiveSchedule/Page";

// Bookmarks to a removed legacy music screen (batch B7) land here. Ids are
// preserved across the catalogue migration (`music.id` -> `songs.id`,
// `music_albums.id` -> `releases.id`, `music_video.id` -> `music_videos.id`,
// klooma-kb/initiatives/music-catalogue-record/design.md), so a route
// parameter carries straight over to the new record.
function redirectTo(to: string) {
  return function LegacyMusicRedirect() {
    const params = useParams();
    const target = Object.entries(params).reduce(
      (path, [key, value]) => path.replace(`:${key}`, String(value)),
      to
    );
    return (
      <Navigate
        to={target}
        replace
      />
    );
  };
}

// Dashboard
const GeneralDashboard = React.lazy(() => import("./screens/Dashboard/General"));
const Analytics = React.lazy(() => import("./screens/Analytics"));
const Login = React.lazy(() => import("./screens/Login"));
const SetPassword = React.lazy(() => import("./screens/SetPassword"));

// Admin
const Admins = React.lazy(() => import("./screens/Admins/List"));
const AdminPage = React.lazy(() => import("./screens/Admins/Page"));

// Roles
const Roles = React.lazy(() => import("./screens/Roles/List"));
const RolePage = React.lazy(() => import("./screens/Roles/Page"));
const RolePages = React.lazy(() => import("./screens/Roles/PageList"));
const CreateRolePage = React.lazy(() => import("./screens/Roles/CreateRolePage"));

// Users
const Users = React.lazy(() => import("./screens/Users/List"));
// `/users/:id`: numeric is a user's page, anything else an old per-type list
// link redirected to `/users?type=`. `/users/detail/:id` is the old detail URL.
const UserDetail = React.lazy(() =>
  import("./screens/Users/routes").then((module) => ({ default: module.UserDetailOrLegacyList }))
);
const LegacyUserDetail = React.lazy(() =>
  import("./screens/Users/routes").then((module) => ({ default: module.LegacyUserDetailRedirect }))
);

// Fan Club
const FanClubFeatured = React.lazy(() => import("./screens/FanClub/FeaturedList"));
const FanClubDashboard = React.lazy(() => import("./screens/FanClub/Dashboard"));
const FanClubCreator = React.lazy(() => import("./screens/FanClub/Creator"));

// Klooma Subscription (Klooma Premium) - mirrors the Fan Club screens above,
// a plan in place of a creator's club (klooma-kb initiatives/klooma-subscription-ops/spec.md §5).
const KloomaSubscriptionDashboard = React.lazy(
  () => import("./screens/KloomaSubscription/Dashboard")
);
const KloomaSubscriptionPlan = React.lazy(() => import("./screens/KloomaSubscription/Plan"));

// Settings: one tabbed section (Configurations / Alarms / Cache). The old
// `/content-settings` screen was the legacy bitrate ladder, whose admin-api
// endpoints went with the legacy transcoder - it is gone, not moved.
const Settings = React.lazy(() => import("./screens/Settings"));
const SystemStatus = React.lazy(() => import("./screens/SystemStatus"));
const SyncRooms = React.lazy(() => import("./screens/SyncRooms"));
const SyncRoomDetail = React.lazy(() =>
  import("./screens/SyncRooms").then((module) => ({ default: module.SyncRoomDetail }))
);
const AuditLog = React.lazy(() => import("./screens/AuditLog"));
const AdsInquiries = React.lazy(() => import("./screens/AdsInquiries"));
const AdsCreativeReviews = React.lazy(() => import("./screens/AdsCreativeReviews"));
const AdsSocialReports = React.lazy(() => import("./screens/AdsSocialReports"));
const AdsFanClubSponsorships = React.lazy(() => import("./screens/AdsFanClubSponsorships"));
const AdsFastAdAir = React.lazy(() => import("./screens/AdsFastAdAir"));
/** FAST ad air staff page: off unless the build turns it on (ads-fast-air-authority contract A5). */
export const FAST_AD_AIR_PANEL = process.env.REACT_APP_FAST_AD_AIR_ENABLED === "true";
const AdsFastAdAirReinstate = React.lazy(() => import("./screens/AdsFastAdAirReinstate"));
/** Policy-only reinstate screen: off unless the build turns it on (admin #118/#122). */
export const FAST_AD_AIR_REINSTATE_PANEL = process.env.REACT_APP_FAST_AD_AIR_REINSTATE_ENABLED === "true";
const AdsOverview = React.lazy(() => import("./screens/AdsOverview"));
const AdsAccountReviews = React.lazy(() => import("./screens/AdsCreativeReviews/StaffReviewQueuePage").then((module) => ({ default: module.AdsAccountReviews })));
const AdsAssetReviews = React.lazy(() => import("./screens/AdsCreativeReviews/StaffReviewQueuePage").then((module) => ({ default: module.AdsAssetReviews })));
const AdsVerificationCases = React.lazy(() => import("./screens/AdsVerificationCases"));
const AdsFinanceReconciliation = React.lazy(() => import("./screens/AdsFinanceReconciliation"));
const AdsBilling = React.lazy(() => import("./screens/AdsBilling"));
const AdsPlayableReview = React.lazy(() => import("./screens/AdsPlayableReview"));
// Staff playable review (admin #137) stays hidden until the deploy turns it on.
const ADS_PLAYABLE_REVIEW_PANEL = process.env.REACT_APP_ADS_PLAYABLE_REVIEW_ENABLED === "true";
// Staff billing (admin A1-A6) stays hidden until the deploy turns it on.
const ADS_BILLING_PANEL = process.env.REACT_APP_ADS_BILLING_STAFF_ENABLED === "true";
const AdsSocialPolicy = React.lazy(() => import("./screens/AdsSocialPolicy"));
// Social Home ad spacing (admin /ads/social/policy) stays hidden until the deploy turns it on.
const ADS_SOCIAL_POLICY_PANEL = process.env.REACT_APP_ADS_SOCIAL_POLICY_STAFF_ENABLED === "true";
const AdsPodcast = React.lazy(() => import("./screens/AdsPodcast"));
// Podcast ads staff screens (admin #146) stay hidden until the deploy turns them on.
const ADS_PODCAST_PANEL = process.env.REACT_APP_ADS_PODCAST_STAFF_ENABLED === "true";
const AdsRuntimeSwitches = React.lazy(() => import("./screens/AdsRuntimeSwitches"));
const AdsStaffSettings = React.lazy(() => import("./screens/AdsStaffSettings"));
const AdsApprovals = React.lazy(() => import("./screens/AdsApprovals"));
const AdsTeamAccess = React.lazy(() => import("./screens/AdsTeamAccess"));
const AdsMfaResets = React.lazy(() => import("./screens/AdsMfaResets"));
const AdsRevenueShare = React.lazy(() => import("./screens/AdsRevenueShare"));
// Ads settings and the approvals queue (admin /ads/settings, /ads/staff-changes) stay hidden until the deploy turns them on.
const ADS_STAFF_SETTINGS_PANEL = process.env.REACT_APP_ADS_STAFF_SETTINGS_ENABLED === "true";
// Staff team access (ads-team-access-contract §14): off unless the build sets it.
const ADS_TEAM_ACCESS_STAFF_PANEL = process.env.REACT_APP_ADS_TEAM_ACCESS_STAFF_ENABLED === "true";
// Revenue-share terms (admin /ads/revenue-share) stay hidden until the deploy turns them on.
const ADS_REVENUE_SHARE_PANEL = process.env.REACT_APP_ADS_REVENUE_SHARE_ENABLED === "true";
// Ads feature switches (admin /ads/runtime-switches) stay hidden until the deploy turns them on.
const ADS_RUNTIME_SWITCHES_PANEL = process.env.REACT_APP_ADS_RUNTIME_SWITCHES_ENABLED === "true";
const AdsOperations = React.lazy(() => import("./screens/AdsOperations"));
const Lives = React.lazy(() => import("./screens/Lives"));
const RoomReports = React.lazy(() => import("./screens/RoomReports"));
const RoomsOverview = React.lazy(() => import("./screens/LiveSync/RoomsOverview"));
const LiveSyncAnalytics = React.lazy(() => import("./screens/LiveSync/Analytics"));
const LiveDetailPage = React.lazy(() =>
  import("./screens/Lives").then((module) => ({ default: module.LiveDetailPage }))
);
const Discover = React.lazy(() => import("./screens/Discover"));
const GameEditor = React.lazy(() => import("./screens/GameEditor"));
const HeroEditor = React.lazy(() => import("./screens/HeroEditor"));
const MediaJobs = React.lazy(() => import("./screens/MediaJobs"));
const Moderation = React.lazy(() => import("./screens/Moderation"));
// Scheduled talk Lives. Not the legacy "Live Content" screens in ./screens/LiveSchedule.
const ScheduledLives = React.lazy(() => import("./screens/ScheduledLives"));
// FAST channels: the list, and one settings page per channel with a route per section.
const ChannelsList = React.lazy(() => import("./screens/Channels/List"));
const ChannelEditor = React.lazy(() => import("./screens/Channels/Editor"));
// A channel's Feed tab: feed health, sources, tests and failover (S2).
const ChannelFeed = React.lazy(() => import("./screens/Channels/Feed/FeedPage"));
// FAST channel analytics (S9): the page, and a channel's Analytics tab.
const ChannelAnalytics = React.lazy(() => import("./screens/ChannelAnalytics"));
const ChannelAnalyticsTab = React.lazy(() => import("./screens/ChannelAnalytics/ChannelTab"));
// FAST Content Library: broadcast versions, FAST rights and guide metadata per title.
const ContentLibrary = React.lazy(() => import("./screens/ContentLibrary/Library"));
// FAST scheduler: the all-channels day, the week with version history, the month (S4c).
const Schedule = React.lazy(() => import("./screens/Schedule/SchedulePage"));
// FAST playout (S5b): on air and next per programmed channel, overrides, as-run.
const Playout = React.lazy(() => import("./screens/Playout"));
// FAST program guide (S6b): all channels, and one channel at /program-guide/channels/:id.
const ProgramGuide = React.lazy(() => import("./screens/ProgramGuide/GuidePage"));

//Movie
const MovieAuthoringList = React.lazy(() => import("./screens/MovieAuthoring/List"));
const MovieAuthoringEditor = React.lazy(() => import("./screens/MovieAuthoring/Editor"));

const FeaturedMusic = React.lazy(() => import("./screens/MusicFeatures"));

// Music authoring - the new record (authoring.md). Replaces the legacy
// Music/MusicAlbums/MusicVideo/MusicGenres screens, removed in batch B7;
// both read `music:*`/`music_video:*`.
const MusicReleasesList = React.lazy(() => import("./screens/MusicAuthoring/Releases/List"));
const MusicReleaseEditor = React.lazy(() => import("./screens/MusicAuthoring/Releases/Editor"));
// The Songs screen (authoring.md §2, batch B4) - reached from here or from a
// release's tracklist; the same editor either way.
const MusicSongsList = React.lazy(() => import("./screens/MusicAuthoring/Songs/List"));
const MusicSongEditor = React.lazy(() => import("./screens/MusicAuthoring/Songs/Editor"));
// The Music videos screen (authoring.md §3, batch B5) - gated on
// `music_video:*`, the legacy `/music-video` screen's own ability.
const MusicVideosList = React.lazy(() => import("./screens/MusicAuthoring/Videos/List"));
const MusicVideoEditor = React.lazy(() => import("./screens/MusicAuthoring/Videos/Editor"));
// The Labels screen (authoring.md §4, batch B6) - gated on `labels:*`
// (added to permissionMap ahead of need in batch B1).
const MusicLabelsList = React.lazy(() => import("./screens/MusicAuthoring/Labels/List"));
const MusicLabelDetail = React.lazy(() => import("./screens/MusicAuthoring/Labels/Detail"));
// The Genre mapping screen (authoring.md §5, batch B6) - gated on `music:*`.
const MusicGenreMapping = React.lazy(
  () => import("./screens/MusicAuthoring/GenreMapping/GenreMapping")
);

// Redirects for the legacy music screens' bookmarks (batch B7).
const LegacyMusicSongsRedirect = redirectTo("/music/songs");
const LegacyMusicSongEditRedirect = redirectTo("/music/songs/edit/:id/details");
const LegacyMusicReleasesRedirect = redirectTo("/music/releases");
const LegacyMusicReleaseEditRedirect = redirectTo("/music/releases/edit/:id/details");
const LegacyMusicVideosRedirect = redirectTo("/music/videos");
const LegacyMusicVideoEditRedirect = redirectTo("/music/videos/edit/:id/details");
const LegacyMusicGenreMappingRedirect = redirectTo("/music/genre-mapping");

// Bare /fan-club is still linked from the creator view's back button and old
// bookmarks; the sidebar's Dashboard sub-links each have their own section route.
const FanClubDashboardRedirect = redirectTo("/fan-club/overview");

// Bare /klooma-subscription redirects the same way (klooma-kb spec §5).
const KloomaSubscriptionDashboardRedirect = redirectTo("/klooma-subscription/overview");
// A channel link without a section opens on Identity.
const ChannelSettingsRedirect = redirectTo("/channels/:id/identity");

//TV shows - the catalogue record (SDD 2026-09-14). `/series` and the
//`series:*` abilities stay as they are; only what sits behind them changed.
const TvAuthoringList = React.lazy(() => import("./screens/TvAuthoring/List"));
const TvShowEditor = React.lazy(() => import("./screens/TvAuthoring/ShowEditor"));
const TvSeasonPage = React.lazy(() => import("./screens/TvAuthoring/SeasonPage"));
const TvEpisodeEditor = React.lazy(() => import("./screens/TvAuthoring/EpisodeEditor"));

//Podcasts - the podcast record (podcast rebuild phase 5). One lazy import per
//screen; the legacy screens under `screens/Podcast` were deleted with it.
const PodcastAuthoringList = React.lazy(() => import("./screens/PodcastAuthoring/List"));
const PodcastShowEditor = React.lazy(() => import("./screens/PodcastAuthoring/ShowEditor"));
const PodcastSeasonPage = React.lazy(() => import("./screens/PodcastAuthoring/SeasonPage"));
const PodcastEpisodeEditor = React.lazy(() => import("./screens/PodcastAuthoring/EpisodeEditor"));
const PodcastReview = React.lazy(() => import("./screens/PodcastReview"));
const PodcasterApplications = React.lazy(() => import("./screens/PodcasterApplications"));
//Fan letter video review (fan letter video spec §7).
const FanLetterReview = React.lazy(() => import("./screens/FanLetterReview"));

//Transaction
const SonyIngestion = React.lazy(() => import("./screens/Transaction/Sony/List"));
const SonyIngestionDetail = React.lazy(() => import("./screens/Transaction/Sony/Detail"));

// Klooma events (the legacy `live` page). Their list now lives on the Live
// schedule; `/live` sends there, and only an admin without the Live schedule
// still gets the old list. The full editor stays at /live/edit/:id.
const LiveList = React.lazy(() => import("./screens/LiveSchedule/List"));
function KloomaEventsEntry(props: any) {
  const { userAccessPages } = useContext(UserContext);
  const readable = (id: string) => (userAccessPages || []).some((page: any) => page.page_id === id && page.read);
  if (readable("scheduled_lives")) {
    return (
      <Navigate
        to="/live-schedule?kind=event"
        replace
      />
    );
  }
  if (!readable("live")) {
    return (
      <Navigate
        to="/403"
        replace
      />
    );
  }
  return <LiveList {...props} />;
}

// styles

/**
 * The page a screen renders into.
 *
 * It used to paint itself `rgb(238,242,246)` and round its top-left corner,
 * so every screen appeared as a white card floating on a grey plate - a
 * screen inside a screen. The page is white and flush now; the only edge is
 * the sidebar's hairline. Its offsets are px, not `vw`, so opening and closing
 * the drawer moves the content by exactly the drawer's width.
 */
const MainStyle = styled("main", {
  shouldForwardProp: (prop) => prop !== "open" && prop !== "isauth",
})(({ theme, open, isauth }: any) => {
  const location = useLocation();
  if (location.pathname.includes("403") || location.pathname.includes("set-password")) {
    return { width: "100%" };
  }

  if (isauth !== 1) {
    return { width: "100%" };
  }

  return {
    marginTop: `${HEADER_HEIGHT}px`,
    minHeight: `calc(100vh - ${HEADER_HEIGHT}px)`,
    backgroundColor: theme.palette.background.default,
    padding: "28px 32px 48px",
    transition: theme.transitions.create("margin", {
      easing: open ? theme.transitions.easing.easeOut : theme.transitions.easing.sharp,
      duration: open
        ? theme.transitions.duration.enteringScreen
        : theme.transitions.duration.leavingScreen,
    }),
    ...(open
      ? { marginLeft: 0, width: `calc(100% - ${SIDEBAR_WIDTH}px)` }
      : {
          width: "100%",
          [theme.breakpoints.up("md")]: { marginLeft: `-${SIDEBAR_WIDTH}px` },
        }),
    // One key: a second `down("md")` entry silently replaced the first, which
    // is how the narrow padding above used to be lost.
    [theme.breakpoints.down("md")]: { marginLeft: 0, width: "100%", padding: "20px 16px 40px" },
  };
});

export default function Navigation() {
  const theme = useTheme();
  const { user, setContactUsCount, logout } = useContext(UserContext);
  const [leftDrawerOpened, setLeftDrawerOpened] = useState(getDrawerStatus());
  // Below `md` the sidebar is an overlay over the page, so it starts closed
  // there. The initial state only covered a narrow window at load; narrowing a
  // window whose drawer was open left the overlay covering the page. Widening
  // again restores the desktop default.
  const isNarrow = useMediaQuery(theme.breakpoints.down("md"), { noSsr: true });
  useEffect(() => {
    setLeftDrawerOpened(isNarrow ? false : getDrawerStatus());
  }, [isNarrow]);
  // @ts-ignore
  window.handleLogout = logout;

  useEffect(() => {
    return;
    (async () => {
      if (user) {
        if (localStorage.getItem("user")) {
          try {
            const countRes = await sendRequest.get({ url: "form/contact-us/open-count" });
            setContactUsCount(countRes.data);
          } catch (error) {
            console.warn("error at count api", error);
          }
        }
      }
    })();
  }, [user]);

  function getDrawerStatus() {
    if (window.innerWidth < 1024) {
      return false;
    }

    let status: any = localStorage.getItem("left_drawer_status");
    if (!status) {
      status = window.innerWidth > 1024;
    }
    return status;
  }

  const handleLeftDrawerToggle = () => {
    setLeftDrawerOpened(!leftDrawerOpened);
  };

  const routes = [
    //Dashboard
    {
      path: "/",
      pageName: "dashboard",
      component: GeneralDashboard,
    },
    {
      // Overview/Content/Revenue/Integrity, one page with in-page tabs. Same
      // `reports` ability as the General Dashboard above.
      path: "/analytics",
      pageName: "analytics",
      component: Analytics,
    },
    //User Routes
    {
      // One list; type, standing and the rest are query-string filters.
      path: "/users",
      pageName: "customers",
      component: Users,
    },
    {
      path: "/users/:id",
      pageName: "customers",
      component: UserDetail,
    },
    {
      path: "/users/detail/:id",
      pageName: "customers",
      component: LegacyUserDetail,
    },
    {
      // The banner on the web app's Fan Clubs page. Guarded by the customers
      // permission because what is being chosen is a creator account.
      path: "/fan-club-featured",
      pageName: "customers",
      component: FanClubFeatured,
    },
    {
      id: "fan_club_dashboard_redirect",
      path: "/fan-club",
      pageName: "fan_club",
      component: FanClubDashboardRedirect,
    },
    {
      // Membership stats and the price. Its own ability: it shows revenue.
      // One entry, not one per section: the section arrives as a route
      // PARAMETER (`FanClubDashboard` reads it with `useParams()`), same as
      // the movie/TV/music editors' `:section`.
      id: "fan_club_dashboard",
      path: "/fan-club/:section",
      pageName: "fan_club",
      component: FanClubDashboard,
    },
    {
      // One creator's club: overview, members, analytics, revenue.
      path: "/fan-club/creators/:userId",
      pageName: "fan_club",
      component: FanClubCreator,
    },
    {
      id: "klooma_subscription_dashboard_redirect",
      path: "/klooma-subscription",
      pageName: "klooma_subscription",
      component: KloomaSubscriptionDashboardRedirect,
    },
    {
      // Membership stats and each plan's price. Its own ability: it shows
      // revenue. One entry, not one per section - the section arrives as a
      // route PARAMETER, same pattern as the fan club dashboard above.
      id: "klooma_subscription_dashboard",
      path: "/klooma-subscription/:section",
      pageName: "klooma_subscription",
      component: KloomaSubscriptionDashboard,
    },
    {
      // One plan in detail: overview, members, analytics, revenue.
      path: "/klooma-subscription/plans/:planId",
      pageName: "klooma_subscription",
      component: KloomaSubscriptionPlan,
    },

    //Admin Routes
    {
      path: "/admins",
      pageName: "admins",
      component: Admins,
    },
    {
      path: "/admins/create",
      pageName: "admins",
      component: AdminPage,
    },
    {
      path: "/admins/edit/:id",
      pageName: "admins",
      component: AdminPage,
    },

    //Role Routes
    {
      path: "/roles",
      pageName: "roles",
      component: Roles,
    },
    {
      path: "/roles/create",
      pageName: "roles",
      component: RolePage,
    },
    {
      path: "/roles/edit/:id",
      pageName: "roles",
      component: RolePage,
    },

    {
      path: "/roles/pages",
      pageName: "roles",
      component: RolePages,
    },
    {
      path: "/roles/pages/create",
      pageName: "roles",
      component: CreateRolePage,
    },
    {
      path: "/roles/pages/edit/:id",
      pageName: "roles",
      component: CreateRolePage,
    },

    //Movie Routes
    {
      path: "/movies",
      pageName: "movies",
      component: MovieAuthoringList,
    },
    // Create happens in a modal on the list screen, not a route - there is
    // nothing left to ask on a page of its own once title and production company are in.
    // Each editor section is its own flat route (no <Outlet>, matching every
    // other route here) so back, forward and a deep link all work.
    {
      // One entry, not one per section: the section has to arrive as a route
      // PARAMETER, because `Editor` reads it with `useParams()`. Six routes
      // with the section written literally into the path left `section`
      // undefined, which sent the editor into a redirect loop and rendered a
      // blank page. Still flat, still `pageName: "movies"`, so
      // `ProtectedRoute`'s `cloneElement` permission injection is unaffected.
      id: "edit_movie",
      path: "/movies/edit/:id/:section",
      pageName: "movies",
      component: MovieAuthoringEditor,
    },

    //TV Series Routes Start
    {
      path: "/series",
      pageName: "series",
      component: TvAuthoringList,
    },
    // The season page and the episode editor are declared ahead of the show
    // editor's `/:section`, so the order a reader sees matches the order the
    // paths are meant to be tried.
    {
      id: "tv_season",
      path: "/series/edit/:id/seasons/:seasonId",
      pageName: "series",
      component: TvSeasonPage,
    },
    {
      id: "edit_tv_episode",
      path: "/series/edit/:id/episodes/:episodeId/:section",
      pageName: "series",
      component: TvEpisodeEditor,
    },
    {
      // One entry, not one per section: the section arrives as a route
      // PARAMETER, because `ShowEditor` reads it with `useParams()`. Its own
      // id, not the movie editor's - `/series/edit/:id` used to declare
      // `edit_movie`, duplicating the real movie editor's.
      id: "edit_tv_show",
      path: "/series/edit/:id/:section",
      pageName: "series",
      component: TvShowEditor,
    },
    //TV Series Routes End

    //Podcast Routes Start - the TV shape: list, show editor by section,
    //season page, episode editor by section. Season and episode are declared
    //ahead of the show editor's `/:section`.
    {
      path: "/podcast",
      pageName: "podcast",
      component: PodcastAuthoringList,
    },
    {
      id: "podcast_season",
      path: "/podcast/edit/:id/seasons/:seasonId",
      pageName: "podcast",
      component: PodcastSeasonPage,
    },
    {
      id: "edit_podcast_episode",
      path: "/podcast/edit/:id/episodes/:episodeId/:section",
      pageName: "podcast",
      component: PodcastEpisodeEditor,
    },
    {
      id: "edit_podcast_show",
      path: "/podcast/edit/:id/:section",
      pageName: "podcast",
      component: PodcastShowEditor,
    },
    {
      path: "/podcast-review",
      pageName: "podcast_review",
      component: PodcastReview,
    },
    {
      path: "/podcaster-applications",
      pageName: "podcaster_applications",
      component: PodcasterApplications,
    },
    //Podcast Routes End

    //Live Routes
    {
      path: "/live",
      // Either page opens it: the entry sends a Live schedule reader there.
      pageName: "live_entry",
      component: KloomaEventsEntry,
    },
    {
      path: "/live/create",
      pageName: "live",
      component: LiveEdit,
    },
    {
      path: "/live/edit/:id",
      pageName: "live",
      component: LiveEdit,
    },

    // Legacy music screens (batch B7 removed them): redirect their bookmarks
    // to the matching new screen. `:id` carries over unchanged - ids are
    // preserved across the catalogue migration.
    {
      id: "legacy_music_redirect",
      path: "/music",
      pageName: "music",
      component: LegacyMusicSongsRedirect,
    },
    {
      id: "legacy_music_create_redirect",
      path: "/music/create",
      pageName: "music",
      component: LegacyMusicSongsRedirect,
    },
    {
      id: "legacy_music_edit_redirect",
      path: "/music/edit/:id",
      pageName: "music",
      component: LegacyMusicSongEditRedirect,
    },
    {
      id: "legacy_music_genre_redirect",
      path: "/music-genres",
      pageName: "music",
      component: LegacyMusicGenreMappingRedirect,
    },
    {
      id: "legacy_music_album_redirect",
      path: "/music-album",
      pageName: "music",
      component: LegacyMusicReleasesRedirect,
    },
    {
      id: "legacy_music_album_create_redirect",
      path: "/music-album/create",
      pageName: "music",
      component: LegacyMusicReleasesRedirect,
    },
    {
      id: "featured_content",
      path: "/featured-content",
      pageName: "music",
      component: FeaturedMusic,
    },
    {
      id: "legacy_music_album_edit_redirect",
      path: "/music-album/edit/:id",
      pageName: "music",
      component: LegacyMusicReleaseEditRedirect,
    },

    //Music authoring Routes - the new record (authoring.md batch B1)
    {
      id: "music_releases",
      path: "/music/releases",
      pageName: "music",
      component: MusicReleasesList,
    },
    // One route, `:section` as a param, matching the movie/TV editors - the
    // section body is not built until batch B2, but the URL shape is settled
    // now so B2 does not have to move anything.
    {
      id: "edit_music_release",
      path: "/music/releases/edit/:id/:section",
      pageName: "music",
      component: MusicReleaseEditor,
    },

    // The Songs screen (authoring.md batch B4) - same shape/route pattern as Releases.
    {
      id: "music_songs",
      path: "/music/songs",
      pageName: "music",
      component: MusicSongsList,
    },
    {
      id: "edit_music_song",
      path: "/music/songs/edit/:id/:section",
      pageName: "music",
      component: MusicSongEditor,
    },

    // The Music videos screen (authoring.md batch B5) - same shape/route
    // pattern as Releases/Songs, gated on `music_video:*`.
    {
      id: "music_videos",
      path: "/music/videos",
      pageName: "music_video",
      component: MusicVideosList,
    },
    {
      id: "edit_music_video",
      path: "/music/videos/edit/:id/:section",
      pageName: "music_video",
      component: MusicVideoEditor,
    },

    // The Labels screen (authoring.md §4, batch B6) - label accounts and relations.
    {
      id: "music_labels",
      path: "/music/labels",
      pageName: "labels",
      component: MusicLabelsList,
    },
    {
      id: "music_label_detail",
      path: "/music/labels/:id",
      pageName: "labels",
      component: MusicLabelDetail,
    },

    // The Genre mapping screen (authoring.md §5, batch B6). Its genre picker
    // also needs `settings:read` (`genre/listfordropdown`), so `music`'s
    // permissionMap entry carries `settings: false` (movies' own pattern)
    // purely to populate that read without requiring it for the route.
    {
      id: "music_genre_mapping",
      path: "/music/genre-mapping",
      pageName: "music",
      component: MusicGenreMapping,
    },

    // Legacy Music Video routes (batch B7 removed the screen): redirect.
    {
      id: "legacy_music_video_redirect",
      path: "/music-video",
      pageName: "music_video",
      component: LegacyMusicVideosRedirect,
    },
    {
      id: "legacy_music_video_create_redirect",
      path: "/music-video/create",
      pageName: "music_video",
      component: LegacyMusicVideosRedirect,
    },
    {
      id: "legacy_music_video_edit_redirect",
      path: "/music-video/edit/:id",
      pageName: "music_video",
      component: LegacyMusicVideoEditRedirect,
    },
    {
      path: "/sony-ingestion",
      pageName: "sony_ingestion",
      component: SonyIngestion,
    },
    {
      path: "/sony-ingestion/detail/:id",
      pageName: "sony_ingestion",
      component: SonyIngestionDetail,
    },

    {
      // The whole settings section; the tab is `?tab=`.
      path: "/settings",
      pageName: "settings",
      component: Settings,
    },
    {
      path: "/system-status",
      pageName: "configurations",
      component: SystemStatus,
    },
    { path: "/rooms-overview", pageName: "rooms_overview", component: RoomsOverview },
    { path: "/live-sync-analytics", pageName: "live_sync_analytics", component: LiveSyncAnalytics },
    {
      path: "/audit-log",
      pageName: "audit_log",
      component: AuditLog,
    },
    { path: "/ads/inquiries", pageName: "ads_inquiries", component: AdsInquiries },
    { path: "/ads/overview", pageName: "ads_provider_ops", component: AdsOverview },
    { path: "/ads/account-reviews", pageName: "ads_review", component: AdsAccountReviews },
    { path: "/ads/asset-reviews", pageName: "ads_review", component: AdsAssetReviews },
    { path: "/ads/creative-reviews", pageName: "ads_review", component: AdsCreativeReviews },
    { path: "/ads/social-reports", pageName: "ads_review", component: AdsSocialReports },
    { path: "/ads/fan-club-sponsorships", pageName: "ads_operations", component: AdsFanClubSponsorships },
    ...(ADS_RUNTIME_SWITCHES_PANEL ? [{ path: "/ads/feature-switches", pageName: "ads_operations", component: AdsRuntimeSwitches }] : []),
    ...(ADS_SOCIAL_POLICY_PANEL ? [{ path: "/ads/social-policy", pageName: "ads_operations", component: AdsSocialPolicy }] : []),
    ...(FAST_AD_AIR_PANEL ? [{ path: "/ads/fast-ad-air", pageName: "ads_operations", component: AdsFastAdAir }] : []),
    ...(FAST_AD_AIR_REINSTATE_PANEL ? [{ path: "/ads/fast-ad-air/reinstate", pageName: "ads_policy", component: AdsFastAdAirReinstate }] : []),
    { path: "/ads/verification", pageName: "ads_review", component: AdsVerificationCases },
    { path: "/ads/finance/reconciliation", pageName: "ads_finance", component: AdsFinanceReconciliation },
    ...(ADS_BILLING_PANEL ? [{ path: "/ads/billing", pageName: "ads_finance", component: AdsBilling }] : []),
    ...(ADS_PLAYABLE_REVIEW_PANEL ? [{ path: "/ads/playables", pageName: "ads_playable_review", component: AdsPlayableReview }] : []),
    ...(ADS_STAFF_SETTINGS_PANEL ? [{ path: "/ads/settings", pageName: "ads_staff_settings", component: AdsStaffSettings },
      { path: "/ads/approvals", pageName: "ads_staff_settings", component: AdsApprovals }] : []),
    ...(ADS_REVENUE_SHARE_PANEL ? [{ path: "/ads/revenue-share", pageName: "ads_revenue_share", component: AdsRevenueShare }] : []),
    ...(ADS_TEAM_ACCESS_STAFF_PANEL ? [{ path: "/ads/team-access", pageName: "ads_team_access", component: AdsTeamAccess },
      { path: "/ads/mfa-resets", pageName: "ads_account_security", component: AdsMfaResets }] : []),
    ...(ADS_PODCAST_PANEL ? [{ path: "/ads/podcast", pageName: "ads_podcast", component: AdsPodcast }] : []),
    { path: "/ads/operations", pageName: "ads_provider_ops", component: AdsOperations },
    {
      path: "/sync-rooms",
      pageName: "sync_rooms",
      component: SyncRooms,
    },
    {
      path: "/sync-rooms/:roomId",
      pageName: "sync_rooms",
      component: SyncRoomDetail,
    },
    {
      path: "/live-schedule",
      pageName: "scheduled_lives",
      component: ScheduledLives,
    },
    { path: "/channels", pageName: "channels", component: ChannelsList },
    // Before `:section` so `feed` is never read as a settings section.
    { path: "/channels/:id/feed", pageName: "channels", component: ChannelFeed },
    { path: "/channels/:id/analytics", pageName: "channel_analytics", component: ChannelAnalyticsTab },
    { path: "/channel-analytics", pageName: "channel_analytics", component: ChannelAnalytics },
    // Like the movie editor: one flat route whose section is a parameter.
    { path: "/channels/:id/:section", pageName: "channels", component: ChannelEditor },
    { path: "/channels/:id", pageName: "channels", component: ChannelSettingsRedirect },
    // The open title is `?item=movie:16`, so the drawer is deep-linkable.
    { path: "/content-library", pageName: "content_library", component: ContentLibrary },
    // The day is `?date=2026-10-17&tz=…`; the week takes any day in it. One shell for all three.
    { path: "/schedule", pageName: "channel_schedule", component: Schedule },
    { path: "/schedule/week", pageName: "channel_schedule", component: Schedule },
    { path: "/schedule/month", pageName: "channel_schedule", component: Schedule },
    // The channel editor (S4b) takes this slot; until then it points back at the day view.
    { path: "/schedule/channels/:id", pageName: "channel_schedule", component: Schedule },
    // The open channel's drawer is `?channel=82`.
    { path: "/playout", pageName: "playout", component: Playout },
    // The window, zone, flag filter and search are in the query string (`?date=2026-10-17&tz=…&flag=stale`).
    { path: "/program-guide", pageName: "program_guide", component: ProgramGuide },
    { path: "/program-guide/channels/:id", pageName: "program_guide", component: ProgramGuide },
    { path: "/lives", pageName: "lives", component: Lives },
    { path: "/lives/:roomId", pageName: "lives", component: LiveDetailPage },
    { path: "/room-reports", pageName: "room_reports", component: RoomReports },
    { path: "/discover/movies", pageName: "discover_movies", component: Discover },
    { path: "/games-editor", pageName: "game_editor", component: GameEditor },
    { path: "/hero-editor", pageName: "hero_editor", component: HeroEditor },
    { path: "/discover/tv", pageName: "discover_tv", component: Discover },
    {
      // Kept as its own route rather than a redirect so an admin who holds
      // `configurations` but not `settings` still has a door in - the guard
      // reads `pageName`, and the screen hides the tabs they cannot use.
      path: "/settings/configurations",
      pageName: "configurations",
      component: Settings,
    },
    {
      path: "/content-cache",
      pageName: "settings",
      component: () => (
        <Navigate
          to="/settings?tab=cache"
          replace
        />
      ),
    },
    {
      path: "/media-jobs",
      pageName: "media_jobs",
      component: MediaJobs,
    },
    {
      path: "/moderation",
      pageName: "moderation",
      component: Moderation,
    },
    {
      path: "/fan-letter-review",
      pageName: "fan_letter_review",
      component: FanLetterReview,
    },
  ];

  return (
    <Router>
      {/* Above <Routes>, so a bulk upload outlives the screen that started it. */}
      <UploadManagerProvider>
        <Box sx={{ display: "flex" }}>
          <CssBaseline />
          <NavigationBar
            user={user}
            leftDrawerOpened={leftDrawerOpened}
            handleLeftDrawerToggle={handleLeftDrawerToggle}
            closeOverlayDrawer={() => isNarrow && setLeftDrawerOpened(false)}
            theme={theme}
          />

          <MainStyle
            theme={theme}
            // @ts-ignore
            open={leftDrawerOpened}
            isauth={user ? 1 : 0}
          >
            <Modal />
            <Loading />
            <Routes>
              {routes.map((route) => (
                <Route
                  id={route.id}
                  key={route.id || "route-item-" + route.path}
                  path={route.path}
                  element={
                    <ProtectedRoute
                      accessPageName={route.pageName}
                      routes={routes}
                      // @ts-ignore
                      component={<route.component />}
                    />
                  }
                />
              ))}

              <Route
                path="/login"
                element={<Login />}
              />

              {/* The admin invitation and password-reset mails link here. */}
              <Route
                path="/set-password/:id/:token"
                element={<SetPassword />}
              />

              <Route
                path="/403"
                element={<ForbiddenPage />}
              />
              <Route
                path="/*"
                element={<div>Page Not Found</div>}
              />
            </Routes>
          </MainStyle>
        </Box>
      </UploadManagerProvider>
    </Router>
  );
}

const NavigationBar = ({
  user,
  leftDrawerOpened,
  handleLeftDrawerToggle,
  closeOverlayDrawer,
  theme,
}) => {
  const location = useLocation();

  // On a narrow window a menu tap should land on the page, not leave the
  // overlay drawer covering it. Only the path matters, so this runs once per
  // navigation.
  useEffect(() => {
    closeOverlayDrawer();
  }, [location.pathname]);
  // @ts-ignore
  window.navigate = useNavigate();

  const { setLoading, setModal } = useContext(MainContext);
  // @ts-ignore
  window.setLoading = setLoading;
  // @ts-ignore
  window.setModal = setModal;

  if (!user || location.pathname.includes("403") || location.pathname.includes("set-password")) {
    return null;
  }

  return (
    <>
      <AppBar
        enableColorOnDark
        position="fixed"
        color="inherit"
        elevation={0}
        sx={{
          // Above the drawer, so the bar reads as one line across the top
          // rather than being met by the sidebar's own edge.
          zIndex: (t) => t.zIndex.drawer + 1,
          backgroundColor: "background.default",
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <Toolbar sx={{ minHeight: `${HEADER_HEIGHT}px !important`, px: { xs: 2, md: 2.5 } }}>
          <Header handleLeftDrawerToggle={handleLeftDrawerToggle} />
        </Toolbar>
      </AppBar>
      <Sidebar
        drawerOpen={leftDrawerOpened}
        drawerToggle={handleLeftDrawerToggle}
      />
    </>
  );
};

const ProtectedRoute = ({ accessPageName, component, routes }) => {
  const { user, userAccessPages } = useContext(UserContext);
  const location = useLocation();
  let permissionList = {};

  if (!user) {
    return (
      <Navigate
        to="/login"
        replace
      />
    );
  }

  let permissionFlag = false; // !! redirect to 403 if its true
  const permissionConfig = permissionMap[accessPageName];

  // !! if the page's permission config is not saved in to "permissionConfig", it will redirect to 403
  if (!permissionConfig) {
    permissionFlag = true;
    console.warn("You need to save the configuration for" + accessPageName);
  } else {
    for (const key in permissionConfig) {
      const permission = userAccessPages.find((page: any) => page.page_id === key);
      permissionList[key] = permission;

      if (permissionConfig[key] && !(permissionConfig[key] === "write" ? permission?.write : permission?.read)) {
        permissionFlag = true;
        break;
      }
    }
    // A config with no required page opens to a holder of any one of them.
    const keys = Object.keys(permissionConfig);
    if (!permissionFlag && !keys.some((key) => permissionConfig[key]) && !keys.some((key) => permissionList[key]?.read || permissionList[key]?.write)) {
      permissionFlag = true;
    }
  }

  if (permissionFlag) {
    // `/` is where sign-in sends everyone, so a role without the dashboard
    // lands on its first readable page there. A deep link keeps the 403.
    return (
      <Navigate
        to={location.pathname === "/" ? firstReadablePath(userAccessPages, routes) : "/403"}
        replace
      />
    );
  }

  return React.cloneElement(component, {
    permission: permissionList,
  });
};
